import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

const PULL_TRIGGER_PX = 70;
const MAX_PULL_PX = 120;
const DAMPING = 0.5;
const INTENT_THRESHOLD_PX = 6;

interface UsePullToRefreshArgs {
  /** Ref to the scrolling container; gesture only starts when scrollTop=0. */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** Async refetch to invoke when the gesture completes past threshold. */
  onRefresh: () => Promise<unknown>;
}

interface UsePullToRefreshResult {
  /** Current pull distance in px; 0 when no gesture in progress. */
  pullDistance: number;
  /** True while the refresh promise is in flight. */
  refreshing: boolean;
  /** Trigger threshold in px (so the view can render "Release to refresh"). */
  triggerPx: number;
}

/**
 * usePullToRefresh — iOS-style pull-to-refresh wired through native touch
 * events on the scroll container directly.
 *
 * Per ChatGPT R1 consult 2026-05-03: pointer events are flaky on iOS for
 * scroll/rubber-band gestures. touchstart/touchmove/touchend is the more
 * reliable control surface. We attach with `addEventListener` (not React
 * synthetic) so we can pass `passive: false` and call preventDefault
 * during the pull, which keeps the iOS rubber-band from stealing the
 * gesture and routing it to the page bounce.
 *
 * Constraints:
 *   - Only fires when scrollContainer.scrollTop <= 0 at gesture start
 *   - Only proceeds if movement is downward AND > 6px (intent threshold)
 *   - Damped at 50% so the bar doesn't sail off-screen
 *   - Capped at 120px so a long pull doesn't push the entire UI down
 *   - Auto-cancels on touch leaving the start area
 */
export function usePullToRefresh({
  scrollContainerRef,
  onRefresh,
}: UsePullToRefreshArgs): UsePullToRefreshResult {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Refs hold the latest values for the native event listeners (which
  // close over the function once at attach time).
  const pullDistanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const updatePull = useCallback((next: number) => {
    pullDistanceRef.current = next;
    setPullDistance(next);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let startY: number | null = null;
    let locked = false;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (el.scrollTop > 0) return;
      const t = e.touches[0];
      if (!t) return;
      startY = t.clientY;
      locked = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY === null) return;
      const t = e.touches[0];
      if (!t) return;
      const delta = t.clientY - startY;

      // Reset if user scrolled the container in the meantime
      if (el.scrollTop > 0) {
        startY = null;
        updatePull(0);
        return;
      }

      if (delta <= 0) {
        // Upward — let native scroll happen, abort the PtR gesture
        updatePull(0);
        return;
      }

      // Establish horizontal-vs-vertical intent
      if (!locked) {
        if (delta < INTENT_THRESHOLD_PX) return;
        locked = true;
      }

      // Prevent iOS rubber-band stealing the gesture once we're locked in
      if (e.cancelable) e.preventDefault();

      const damped = Math.min(MAX_PULL_PX, Math.round(delta * DAMPING));
      updatePull(damped);
    };

    const onTouchEnd = () => {
      const distance = pullDistanceRef.current;
      startY = null;
      locked = false;
      updatePull(0);
      if (distance >= PULL_TRIGGER_PX && !refreshingRef.current) {
        refreshingRef.current = true;
        setRefreshing(true);
        void onRefreshRef
          .current()
          .finally(() => {
            refreshingRef.current = false;
            setRefreshing(false);
          });
      }
    };

    // passive: false on touchmove so we can call preventDefault.
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [scrollContainerRef, updatePull]);

  return {
    pullDistance,
    refreshing,
    triggerPx: PULL_TRIGGER_PX,
  };
}
